import { useGivingAccess } from "@/features/giving/treasureAccess";
import { useServiceNavigation } from "@/features/authorization/hooks/useServiceNavigation";
import { Link, Outlet } from "react-router-dom";
import { PageAccess } from "@/features/authorization/components/PageAccess";
import { canOpenPage } from "@/features/authorization/pageAccess";
import { useQuery } from "@tanstack/react-query";

import { getChurchById } from "@/api/churches";
import { CommandPalette } from "@/components/command/CommandPalette";
import { useAuth } from "@/features/auth/hooks/useAuth";

import { useMinistryNavigation } from "@/features/authorization/hooks/useMinistryNavigation";

import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { GlobalFetchingIndicator } from "./GlobalFetchingIndicator";
import { Header } from "./Header";
import { OfflineBanner } from "./OfflineBanner";
import { Sidebar } from "./Sidebar";

export function AppLayout() {
  const { churchId } = useAuth();
  const giving = useGivingAccess(true);
  const ministries = useMinistryNavigation();
  const { permissions } = useEffectivePermissions();
  const servicesPath = useServiceNavigation();

  const { data: church } = useQuery({
    queryKey: ["church", churchId],
    queryFn: () => getChurchById(churchId!),
    enabled: !!churchId,
  });

  return (
    <div className="min-h-screen bg-slate-950">
      <GlobalFetchingIndicator />
      <OfflineBanner />

      <div className="flex">
        <Sidebar
          ministries={ministries}
          churchName={church?.name ?? ""}
          churchCity={church?.city}
          churchState={church?.state}
        />

        <main className="min-h-screen min-w-0 flex-1">
          <Header />
          <div className="space-y-3 px-5 py-3 text-sm text-indigo-300 md:hidden">
            <nav
              aria-label="Mobile navigation"
              className="flex flex-wrap gap-4"
            >
              {canOpenPage("/", permissions) && <Link to="/">Overview</Link>}
              {servicesPath && <Link to={servicesPath}>Services</Link>}
              {!!giving.data?.permissions.length && <Link to="/treasure">Treasure</Link>}
              {(permissions.has("MINISTRIES_MANAGE") ||
                permissions.has("ROLES_MANAGE")) && (
                <Link
                  to={
                    permissions.has("MINISTRIES_MANAGE")
                      ? "/settings/administration/ministries"
                      : "/settings/administration/roles"
                  }
                >
                  Administration
                </Link>
              )}
              {canOpenPage("/settings/data-import", permissions) && <Link to="/settings/data-import">Data Import</Link>}
              {canOpenPage("/settings/system-health", permissions) && <Link to="/settings/system-health">System Health</Link>}
            </nav>
            {ministries.length > 0 && (
              <section aria-labelledby="mobile-ministry-navigation-heading">
                <h2
                  id="mobile-ministry-navigation-heading"
                  className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  Ministries
                </h2>
                <nav
                  aria-label="Mobile ministry workspaces"
                  className="flex flex-wrap gap-4"
                >
                  {ministries.map((ministry) => (
                    <Link key={ministry.id} to={ministry.to}>
                      {ministry.label}
                    </Link>
                  ))}
                </nav>
              </section>
            )}
          </div>

          <div className="px-5 py-5 lg:px-6 lg:py-5">
            <PageAccess><Outlet /></PageAccess>
          </div>
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}
